import { useEffect, useState, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import classNames from './EditContainer.module.scss';
import classes from 'classnames';
import SVG from 'react-inlinesvg';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import {
  validateFirstName,
  validateLastName,
} from '@/school-management/utils/userDataValidationUtils';
import { trimNamePart } from '@/utils/personName';

import TheButton from '@/components/school-management/TheButton/TheButton';
import Dropdown, { DropdownOption } from './Dropdown/Dropdown';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import Field from './field';
import TextField from './textField';
import DeleteStudent from './DeleteStudent/DeleteStudent';
import { StudentDetails, UserStatusEnum } from '@/school-management/types/response/studentDetails';
import { UpdatedStudentSettingsRequest } from '@/school-management/types/services/studentDetails';
import { studentDetailsService } from '@/school-management/services/studentDetails';
import { icons } from '@/school-management/utils/icons';
import deleteIcon from '@/assets/svg/trash.svg';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';

export interface EditData {
  firstName: string;
  lastName: string;
  interfaceLanguage: string;
  level: number;
}

interface EditContainerProps {
  schoolId: string;
  studentId: string;
  studentDetails: StudentDetails;
  emitSaveEdit?: (studentId: string, newData: EditData, oldData: EditData) => void;
  emitCloseEdit?: () => void;
  emitDeleteStudent?: () => void;
}

const EditContainer = ({
  schoolId,
  studentId,
  studentDetails,
  emitSaveEdit,
  emitCloseEdit,
  emitDeleteStudent,
}: EditContainerProps) => {
  const { t } = useAdminTranslation();
  const { user } = useAppSelector(authSelector);

  const isInterfaceLanguageDisabled = useMemo(() => {
    const fromToLanguageParam =
      user?.metadata?.languagePreferences?.fromToLanguageParams?.[0] || '';
    if (!fromToLanguageParam) return false;
    const [from, to] = fromToLanguageParam.split('-').map(v => v.trim().toLowerCase());
    return Boolean(from) && Boolean(to) && from === to;
  }, [user]);

  const [interfaceLanguageState, setInterfaceLanguageState] = useState<string>('');
  const [levelState, setLevelState] = useState<number>(1);
  const [firstNameState, setFirstNameState] = useState<string>('');
  const [lastNameState, setLastNameState] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<string>('');
  const [lastNameError, setLastNameError] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');
  const [showLevelConfirmModal, setShowLevelConfirmModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isLoadingProgress, setIsLoadingProgress] = useState<boolean>(false);

  useEffect(() => {
    setInterfaceLanguageState(studentDetails.studentData?.interfaceLanguage);
    setFirstNameState(studentDetails.studentData?.firstName);
    setLastNameState(studentDetails.studentData?.lastName);
    setLevelState(studentDetails.progress?.level || 1);
  }, [studentDetails]);

  useEffect(() => {
    const error = validateFirstName(firstNameState, studentId?.startsWith('google_'));
    setFirstNameError(error ? error.message : '');
  }, [studentId, firstNameState]);

  useEffect(() => {
    const error = validateLastName(lastNameState, studentId?.startsWith('google_'));
    setLastNameError(error ? error.message : '');
  }, [studentId, lastNameState]);

  useEffect(() => {
    setSaveError('');
    setShowDeleteModal(false);
    setShowLevelConfirmModal(false);
    setIsLoadingProgress(false);
  }, [firstNameState, lastNameState, interfaceLanguageState]);

  const filteredLevels: DropdownOption[] = useMemo(() => {
    return [1, 2, 3, 4, 5].map(level => {
      return {
        label: t(`levelName_${level}`),
        value: level,
      };
    });
  }, [t]);

  const onSave = useCallback(
    async (showLevelConfirm: boolean = true) => {
      setSaveError('');
      setShowLevelConfirmModal(false);

      const newData: EditData = {
        firstName: trimNamePart(firstNameState),
        lastName: trimNamePart(lastNameState),
        interfaceLanguage: interfaceLanguageState,
        level: levelState,
      };

      const oldData: EditData = {
        firstName: studentDetails.studentData?.firstName,
        lastName: studentDetails.studentData?.lastName,
        interfaceLanguage: studentDetails.studentData?.interfaceLanguage,
        level: studentDetails.progress?.level,
      };

      const setting: UpdatedStudentSettingsRequest = {};

      // Check if anything has actually changed
      const hasNameOrLanguageChanged =
        oldData.firstName !== newData.firstName ||
        oldData.lastName !== newData.lastName ||
        oldData.interfaceLanguage !== newData.interfaceLanguage;
      const hasLevelChanged =
        !(studentDetails?.studentData?.userStatus === UserStatusEnum.INACTIVE) &&
        !(studentDetails?.studentData?.userStatus === UserStatusEnum.LEVEL_TEST_INCOMPLETE) &&
        oldData.level !== newData.level;

      // If nothing has changed, just close the modal without showing success notification
      if (!hasNameOrLanguageChanged && !hasLevelChanged) {
        emitCloseEdit?.();
        return;
      }

      // Advancing a student who completed their level but is blocked from progressing by the
      // class's "allow individual advancement" setting preserves their progress (ELL-7733), so
      // the "changing the level will delete progress" warning doesn't apply to this one case.
      const isBlockedLevelCompleteAdvance =
        studentDetails.progress?.totalProgressInLevel === 100 &&
        (studentDetails.studentData?.groupSettings?.allowIndividualAdvancement ?? true) === false &&
        newData.level === oldData.level + 1;

      if (showLevelConfirm && oldData.level && hasLevelChanged && !isBlockedLevelCompleteAdvance) {
        setShowLevelConfirmModal(true);
        return;
      }

      if (oldData.firstName !== newData.firstName) {
        setting.firstName = newData.firstName;
      }

      if (oldData.lastName !== newData.lastName) {
        setting.lastName = newData.lastName;
      }

      if (oldData.interfaceLanguage !== newData.interfaceLanguage) {
        setting.interfaceLanguage = {
          interfaceLanguageCode: newData.interfaceLanguage,
        };
      }

      // Only include level change for active students
      if (hasLevelChanged) {
        setting.changeLevel = {
          newLevel: newData.level.toString(), // Convert number to string, because the backend expects a string
        };
      }

      let isOk: boolean = false;
      setIsLoadingProgress(true);

      try {
        isOk =
          (await studentDetailsService.updateStudentSettings(schoolId, studentId, setting))?.data
            ?.data || false;
        // Wait 2 seconds for DynamoDB stream to update postgres
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (e) {
        console.log('e', e);
      }

      setIsLoadingProgress(false);
      if (isOk) {
        emitSaveEdit?.(studentId, newData, oldData);
        emitCloseEdit?.();
      } else {
        setSaveError('studentDataModal.editForm.errors.anErrorOccurred');
      }
    },
    [
      schoolId,
      studentId,
      emitSaveEdit,
      emitCloseEdit,
      firstNameState,
      lastNameState,
      interfaceLanguageState,
      levelState,
      studentDetails,
    ]
  );

  const onDeleteStudent = useCallback(() => {
    emitDeleteStudent?.();
  }, [emitDeleteStudent]);

  const languageDropdownOptions: DropdownOption[] = useMemo(() => {
    const allLanguages = [studentDetails.studentData?.nativeLanguage];

    const filteredLanguages = allLanguages.filter(lang => lang.toLowerCase() !== 'en');

    const finalLanguages = ['en', ...filteredLanguages];

    return finalLanguages.map(language => ({
      value: language,
      label: (
        <div className={classNames.languageOption}>
          <SVG src={icons[language as keyof typeof icons]} />
          <span>{t(`languages.${language}`)}</span>
        </div>
      ),
    }));
  }, [studentDetails, t]);

  return (
    <div className={classNames.editContainer}>
      <div className={classes(classNames.section, classNames.grid)}>
        <Field
          label={t('studentDataModal.editForm.firstname').toString()}
          error={
            firstNameError && t(`studentDataModal.editForm.errors.${firstNameError}`).toString()
          }
        >
          <TextField value={firstNameState} error={firstNameError} onChange={setFirstNameState} />
        </Field>
        <Field
          label={t('studentDataModal.editForm.lastname').toString()}
          error={lastNameError && t(`studentDataModal.editForm.errors.${lastNameError}`).toString()}
        >
          <TextField value={lastNameState} error={lastNameError} onChange={setLastNameState} />
        </Field>
        {studentDetails.studentData?.email && (
          <Field
            label={t('studentDataModal.editForm.email').toString()}
            className={classNames.gridCell2}
          >
            <TextField value={studentDetails.studentData?.email} disabled={true} />
          </Field>
        )}
        {studentDetails.studentData?.username && !studentDetails.studentData?.email && (
          <Field
            label={t('studentDataModal.editForm.username').toString()}
            className={classNames.gridCell2}
          >
            <TextField value={studentDetails.studentData?.username} disabled={true} />
          </Field>
        )}
      </div>
      {studentDetails.progress?.level && (
        <>
          <div className={classNames.sectionTitle}>{t('studentDataModal.editForm.level')}</div>
          <div className={classNames.section}>
            <div className={classNames.selects}>
              <Field
                label={`${t('studentDataModal.editForm.currentLevel')} - ${t(`levelName_${studentDetails.progress.level}`)}`}
              >
                <Dropdown
                  value={studentDetails.progress?.level !== levelState ? levelState : undefined}
                  options={filteredLevels.filter(level => level.value !== levelState)}
                  onChange={level => {
                    setLevelState(level);
                  }}
                  label={
                    studentDetails.progress?.level !== levelState
                      ? t(`levelName_${levelState}`)
                      : t('studentDataModal.editForm.ChooseAnotherLevel')
                  }
                  type='field'
                />
              </Field>
            </div>
          </div>
        </>
      )}
      <>
        <div className={classNames.sectionTitle}>{t('studentDataModal.editForm.language')}</div>
        <div className={classNames.section}>
          <div className={classNames.selects}>
            <Field label={t('studentDataModal.editForm.interfaceLanguage').toString()}>
              <Dropdown
                value={interfaceLanguageState}
                options={languageDropdownOptions}
                onChange={language => {
                  setInterfaceLanguageState(language);
                }}
                type='field'
                disabled={isInterfaceLanguageDisabled}
              />
            </Field>
          </div>
        </div>
      </>

      <div className={classNames.errorSection}>{saveError && <span>{t(saveError)}</span>}</div>

      <div className={classNames.buttons}>
        <button
          className={classNames.deleteButton}
          onClick={() => {
            setShowDeleteModal(true);
          }}
          aria-label={t('studentDataModal.editForm.deleteStudent').toString()}
        >
          <SVG src={deleteIcon} />
          <span>{t('studentDataModal.editForm.deleteStudent')}</span>
        </button>
        {isLoadingProgress ? (
          <div className={classNames.loader}>
            <AppLoaderCircle />
          </div>
        ) : (
          <>
            <TheButton
              plain
              text={t('studentDataModal.editForm.cancel')}
              emitOnClick={emitCloseEdit}
              className={classNames.cancelButton}
            />
            <TheButton
              className={classNames.saveButton}
              text={t('studentDataModal.editForm.save')}
              shouldRecolor={false}
              emitOnClick={() => {
                onSave(true);
              }}
              disabled={
                !(firstNameState && lastNameState && interfaceLanguageState) ||
                !!firstNameError ||
                !!lastNameError ||
                isLoadingProgress
              }
            />
          </>
        )}
      </div>

      {showLevelConfirmModal &&
        ReactDOM.createPortal(
          <div className={classNames.levelConfirmModal}>
            <div className={classNames.levelConfirmModalContent}>
              <h2>{t('studentDataModal.editForm.areYouSure')}</h2>
              <p>{t('studentDataModal.editForm.changingLevel')}</p>
              <TheButton
                text={t('studentDataModal.editForm.yes')}
                shouldRecolor={false}
                className={classNames.saveButton}
                emitOnClick={() => {
                  onSave(false);
                }}
              />
              <TheButton
                text={t('studentDataModal.editForm.cancel')}
                className={classNames.cancelButton}
                plain={true}
                emitOnClick={() => {
                  setShowLevelConfirmModal(false);
                }}
              />
            </div>
            <div className={classNames.levelConfirmBackdrop} />
          </div>,
          document.getElementById('StudentDataModalOverLayer') as Element
        )}

      {showDeleteModal && (
        <DeleteStudent
          onClose={() => {
            setShowDeleteModal(false);
          }}
          onDelete={onDeleteStudent}
          schoolId={schoolId}
          studentId={studentId}
        />
      )}
    </div>
  );
};

export default EditContainer;
